import { useEffect, useState } from 'react';
import { createResource, getResources } from '../../../router/api';
import toast from 'react-hot-toast';
import Header from '../../dashboard/components/Header';
import Sidebar from '../../dashboard/components/Sidebar';
import { useParams, useNavigate } from 'react-router-dom';

export default function BCCDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [bcc, setBcc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ================= FETCH ================= */
  useEffect(() => {
    if (!id) {
      toast.error('ID du bon de commande manquant');
      setLoading(false);
      return;
    }

    getResources(`bons-commande-clients/${id}`)
      .then(res => setBcc(res.data.data || res.data))
      .catch(() => toast.error('Impossible de charger le bon de commande'))
      .finally(() => setLoading(false));
  }, [id]);

  /* ================= ACTIONS ================= */
  const handleValidate = async () => {
    try {
      await createResource(`bons-commande-clients/${bcc.id}/valider`, {});
      toast.success(`Commande ${bcc.numero} validée`);
      navigate(0);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Validation impossible');
    }
  };

  const handleCancel = async () => {
    try {
      await createResource(`bons-commande-clients/${bcc.id}/annuler`, {});
      toast.success(`Commande ${bcc.numero} annulée`);
      navigate(-1);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Annulation impossible');
    }
  };
  
  const handlePrintBCC = () => {
    if (!bcc) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression");
      return;
    }

    const totalTTC = Number(bcc.total_ttc);
    const totalHT = bcc.total_ht ?? totalTTC / 1.1925;
    const tvaMontant = bcc.total_tva ?? totalTTC - totalHT;
    const remiseGlobale = Number(bcc.remise_globale ?? 0);

    const sousTotalHT = bcc.lignes.reduce(
      (sum: number, l: any) => sum + Number(l.total_ht || 0),
      0
    );

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>BCC ${bcc.numero}</title>
<style>
body { font-family: Arial; margin: 40px; }
table { width:100%; border-collapse:collapse; }
th,td { border:1px solid #ccc; padding:8px; }
th { background:#eee; }
</style>
</head>
<body>
<h2>BON DE COMMANDE CLIENT – ${bcc.numero}</h2>

<p><strong>Client :</strong> ${bcc.client.raison_sociale}</p>
<p><strong>Date :</strong> ${new Date(bcc.date_commande).toLocaleDateString('fr-FR')}</p>

<table>
<thead>
<tr>
<th>Désignation</th>
<th>Qté</th>
<th>PU HT</th>
<th>Total HT</th>
</tr>
</thead>
<tbody>
${bcc.lignes.map((l: any) => `
<tr>
<td>${l.designation}</td>
<td>${l.quantite_commandee}</td>
<td>${l.prix_unitaire_ht}</td>
<td>${l.total_ht}</td>
</tr>
`).join('')}
</tbody>
</table>

<p><strong>Sous-total HT :</strong> ${sousTotalHT}</p>
<p><strong>TVA :</strong> ${tvaMontant}</p>
<p><strong>Total TTC :</strong> ${totalTTC}</p>

</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  /* ================= UTILS ================= */
  const formatPrice = (v: number | string) =>
    new Intl.NumberFormat('fr-FR').format(Number(v)) + ' GNF';

  const sousTotalHT = (bcc?.lignes || []).reduce(
    (s: number, l: any) => s + Number(l.total_ht || 0),
    0
  );


  const getStatusBadge = (statut: string) => {
    const map: Record<string, string> = {
      BROUILLON: 'bg-gray-100 text-gray-800',
      VALIDE: 'bg-blue-100 text-blue-800',
      EN_COURS: 'bg-purple-100 text-purple-800',
      LIVRE_PARTIEL: 'bg-green-100 text-green-800',
      LIVRE: 'bg-green-100 text-green-800',
      ANNULE: 'bg-red-100 text-red-800',
    };
    return map[statut] || 'bg-gray-100 text-gray-700';
  };

  /* ================= STATES ================= */
  if (loading) {
    return <div className="h-screen flex items-center justify-center">Chargement…</div>;
  }

  if (!bcc) {
    return <div className="h-screen flex items-center justify-center text-red-600">BCC introuvable</div>;
  }

  /* ================= RENDER ================= */
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col lg:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto pt-24 p-6">
          <div className="max-w-7xl mx-auto space-y-6">

            {/* HEADER */}
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold">Bon de Commande Client</h1>
                <p className="text-gray-600">N° {bcc.numero}</p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="px-5 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                ← Retour
              </button>
            </div>

            {/* INFOS */}
            <div className="bg-white rounded-xl border p-6 grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold mb-2">Client</h3>
                <p>{bcc.client.raison_sociale}</p>
                <p className="text-sm text-gray-600">{bcc.client.email}</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Commande</h3>
                <p>Date : {new Date(bcc.date_commande).toLocaleDateString('fr-FR')}</p>
                <p>Livraison : {bcc.date_livraison_souhaitee
                  ? new Date(bcc.date_livraison_souhaitee).toLocaleDateString('fr-FR')
                  : '—'}
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Statut</h3>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusBadge(bcc.statut)}`}>
                  {bcc.statut}
                </span>
              </div>
            </div>

            {/* LIGNES */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">Désignation</th>
                    <th className="px-6 py-3 text-center">Qté</th>
                    <th className="px-6 py-3 text-right">PU HT</th>
                    <th className="px-6 py-3 text-right">Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {bcc.lignes.map((l: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="px-6 py-3">{l.designation}</td>
                      <td className="px-6 py-3 text-center">{l.quantite_commandee}</td>
                      <td className="px-6 py-3 text-right">{formatPrice(l.prix_unitaire_ht)}</td>
                      <td className="px-6 py-3 text-right font-semibold">{formatPrice(l.total_ht)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* TOTAUX */}
            <div className="bg-white rounded-xl border p-6 flex justify-end">
              <div className="w-full max-w-md space-y-2">
                <div className="flex justify-between">
                  <span>Sous-total HT</span>
                  <span>{formatPrice(sousTotalHT)}</span>
                </div>
                <div className="flex justify-between">
                  <span>TVA</span>
                  <span>{formatPrice(bcc.total_tva)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg text-teal-600 border-t pt-2">
                  <span>Total TTC</span>
                  <span>{formatPrice(bcc.total_ttc)}</span>
                </div>
              </div>
            </div>

            {/* ACTIONS (IDENTIQUES À LA LISTE) */}
            <ActionButtons
              statut={bcc.statut}
              onEdit={() => navigate(`/bcc/${bcc.id}/edit`)}
              onValidate={handleValidate}
              onCancel={handleCancel}
              onPrint={handlePrintBCC}
            />

          </div>
        </main>
      </div>
    </div>
  );
}

/* ================= ACTION BUTTONS ================= */
function ActionButtons({ statut, onEdit, onValidate, onCancel, onPrint }: any) {
  return (
    <div className="flex justify-end gap-4">
      {statut === 'BROUILLON' && (
        <>
          {/* <button onClick={onEdit} className="px-6 py-3 border rounded-lg hover:bg-gray-50">
            <i className="ri-edit-line mr-2" /> Modifier
          </button> */}
          <button onClick={onValidate} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <i className="ri-check-line mr-2" /> Valider
          </button>
          <button onClick={onCancel} className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700">
            <i className="ri-close-circle-line mr-2" /> Annuler
          </button>
        </>
      )}

      {statut !== 'ANNULE' && (
        <button onClick={onPrint} className="px-6 py-3 bg-gray-700 text-white rounded-lg">
          Imprimer
        </button>
      )}
    </div>
  );
}
